import { useEffect, useState } from "react";
import {
  BookOpen,
  Calendar,
  ClipboardCheck,
  ExternalLink,
  Layers,
  Lightbulb,
  Pencil,
  Trash2,
  X,
} from "lucide-react";
import { useMsal } from "@azure/msal-react";
import type {
  CatalogItem,
  InitiativeItem,
  LibraryItem,
  PlatformItem,
} from "../types/navigator";
import {
  Badge,
  assetVariant,
  complexityVariant,
  priorityVariant,
  statusVariant,
} from "./Badge";
import { Avatar } from "./Avatar";
import { isNew, isRecentlyUpdated, relativeTime } from "../utils/time";
import { useIsAdmin } from "../hooks/useIsAdmin";
import { useGraphToken } from "../auth/useGraphToken";
import { deleteItem } from "../api/graph";
import { EditItemModal } from "./EditItemModal";
import { InitiativeDetails } from "./InitiativeDetails";
import { LibraryDetails } from "./LibraryDetails";
import { PlatformDetails } from "./PlatformDetails";
import { ConfirmDialog } from "./ConfirmDialog";
import { InitiativeReviewActions } from "./InitiativeReviewActions";
import { LikeButton } from "./LikeButton";

type Props = {
  item: CatalogItem;
  onClose: () => void;
  /** Called after a successful save — use to refresh the parent list. */
  onSaved?: () => void;
  /** Called when a review is saved from inside the popup (it stays open). */
  onReviewSaved?: () => void;
};

function KindIcon({ kind, size = 20 }: { kind: CatalogItem["kind"]; size?: number }) {
  if (kind === "library") return <BookOpen size={size} />;
  if (kind === "initiative") return <Lightbulb size={size} />;
  return <Layers size={size} />;
}

function kindLabel(kind: CatalogItem["kind"]): string {
  if (kind === "library") return "Library asset";
  if (kind === "initiative") return "AI Initiative";
  return "AI Platform";
}

function ownerNameFor(item: CatalogItem): string | null {
  if (item.kind === "initiative") return item.submittedBy || null;
  if (item.kind === "library") return item.department || null;
  return null;
}

function ownerLabelFor(item: CatalogItem): string | null {
  if (item.kind === "initiative") return "Submitted by";
  if (item.kind === "library") return "Department";
  return null;
}

function ribbonFor(item: CatalogItem): "new" | "updated" | null {
  // A "New" badge next to a status that already reads "New" looks like a
  // duplicate, so let the status speak for itself.
  if (isNew(item.created)) return (item.status ?? "").trim().toLowerCase() === "new" ? null : "new";
  if (isRecentlyUpdated(item.created, item.modified)) return "updated";
  return null;
}

function LibraryBody({ item }: { item: LibraryItem }) {
  return <LibraryDetails item={item} />;
}

function InitiativeBody({ item }: { item: InitiativeItem }) {
  return <InitiativeDetails item={item} />;
}

function PlatformBody({ item }: { item: PlatformItem }) {
  return <PlatformDetails item={item} />;
}

function ModalBadges({ item }: { item: CatalogItem }) {
  if (item.kind === "library") {
    return (
      <>
        {item.status ? <Badge variant={statusVariant(item.status)}>{item.status}</Badge> : null}
        {item.assetType ? <Badge variant={assetVariant(item.assetType)}>{item.assetType}</Badge> : null}
        {item.aiPlatform ? <Badge variant="outline">{item.aiPlatform}</Badge> : null}
        {item.complexity ? <Badge variant={complexityVariant(item.complexity)}>{item.complexity}</Badge> : null}
        {item.benefitType ? <Badge variant="outline">{item.benefitType}</Badge> : null}
      </>
    );
  }
  if (item.kind === "initiative") {
    return (
      <>
        {item.status ? <Badge variant={statusVariant(item.status)}>{item.status}</Badge> : null}
        {item.priority ? <Badge variant={priorityVariant(item.priority)}>{item.priority} priority</Badge> : null}
        {item.region ? <Badge variant="outline">{item.region}</Badge> : null}
        {item.team ? <Badge variant="outline">{item.team}</Badge> : null}
      </>
    );
  }
  return (
    <>
      {item.status ? <Badge variant={statusVariant(item.status)}>{item.status}</Badge> : null}
      {item.platformType ? <Badge variant="outline">{item.platformType}</Badge> : null}
      {item.complexity ? <Badge variant={complexityVariant(item.complexity)}>{item.complexity}</Badge> : null}
      {item.licenseType ? <Badge variant="outline">{item.licenseType}</Badge> : null}
    </>
  );
}

export function ItemModal({ item, onClose, onSaved, onReviewSaved }: Props) {
  const isAdmin = useIsAdmin();
  const getToken = useGraphToken();
  const { accounts } = useMsal();
  const account = accounts[0];

  const [editOpen, setEditOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const subModalOpen = editOpen || reviewOpen;

  const doDelete = async () => {
    try {
      setDeleteLoading(true);
      setDeleteError(null);
      const token = await getToken();
      await deleteItem(token, item.kind, item.id);
      setDeleteConfirmOpen(false);
      onSaved?.();
      onClose();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Delete failed.");
      setDeleteLoading(false);
    }
  };

  // Lock body scroll for the lifetime of ItemModal, regardless of which
  // sub-modal is active. Escape only closes ItemModal when no sub-modal is open.
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !subModalOpen) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, subModalOpen]);

  const externalUrl = item.kind === "library" ? item.url : undefined;
  const externalLabel = item.kind === "library" ? item.urlLabel : undefined;
  const ribbon = ribbonFor(item);
  const ownerName = ownerNameFor(item);
  const ownerLabel = ownerLabelFor(item);
  const relTime = relativeTime(item.modified ?? item.created);
  const reviewingInitiative = reviewOpen && item.kind === "initiative" ? item as InitiativeItem : null;

  return (
    <>
      {/* Main modal — hidden while edit/review sub-modal is active */}
      {!subModalOpen ? (
        <div
          className="modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          onClick={onClose}
        >
          <div
            className={`modal modal-${item.kind}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-head">
              <span className="modal-accent" aria-hidden />
              <div className={`modal-head-icon icon-${item.kind}`} aria-hidden>
                <KindIcon kind={item.kind} size={20} />
              </div>
              <div className="modal-head-text">
                <span className="modal-kind">{kindLabel(item.kind)}</span>
                <div className="modal-title-row">
                  <h3 id="modal-title" className="modal-title">{item.title}</h3>
                  {item.kind === "library" ? <LikeButton item={item} /> : null}
                </div>
                <div className="modal-badges">
                  <ModalBadges item={item} />
                </div>
              </div>
              <button
                className="modal-close"
                onClick={onClose}
                aria-label="Close"
                type="button"
              >
                <X size={18} />
              </button>
            </div>

            {(ownerName || relTime || ribbon) ? (
              <div className="modal-subhead">
                {ownerName ? (
                  <div className="modal-owner">
                    <Avatar name={ownerName} size={26} title={`${ownerLabel}: ${ownerName}`} />
                    <div className="modal-owner-text">
                      <span className="modal-owner-label">{ownerLabel}</span>
                      <span className="modal-owner-name">{ownerName}</span>
                    </div>
                  </div>
                ) : null}
                {(relTime || ribbon) ? (
                  <div className="modal-updated" title={item.modified ?? item.created}>
                    {relTime ? (
                      <>
                        <Calendar size={13} aria-hidden />
                        <span>Updated {relTime}</span>
                      </>
                    ) : null}
                    {ribbon ? (
                      <span className={`card-ribbon-inline card-ribbon-${ribbon}`}>
                        {ribbon === "new" ? "New" : "Updated"}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="modal-body">
              {item.kind === "library" ? <LibraryBody item={item} /> : null}
              {item.kind === "initiative" ? <InitiativeBody item={item} /> : null}
              {item.kind === "platform" ? <PlatformBody item={item} /> : null}
            </div>

            <div className="modal-foot">
              {isAdmin ? (
                <div className="modal-admin-actions">
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    onClick={() => setEditOpen(true)}
                  >
                    <Pencil size={13} aria-hidden /> Edit
                  </button>
                  {item.kind === "initiative" ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost"
                      onClick={() => setReviewOpen(true)}
                    >
                      <ClipboardCheck size={13} aria-hidden /> Review
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost btn-danger"
                    onClick={() => setDeleteConfirmOpen(true)}
                  >
                    <Trash2 size={13} aria-hidden /> Delete
                  </button>
                </div>
              ) : null}
              {externalUrl ? (
                <a className="btn" href={externalUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink size={14} aria-hidden />
                  Open {externalLabel || "link"}
                </a>
              ) : null}
              <button className="btn btn-primary" onClick={onClose} type="button">Close</button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Edit sub-modal — replaces main modal */}
      {editOpen ? (
        <EditItemModal
          item={item}
          siblings={[]}
          onClose={() => setEditOpen(false)}
          onSaved={onSaved ?? onClose}
        />
      ) : null}

      {/* Review sub-modal — replaces main modal (initiatives only) */}
      {reviewingInitiative ? (
        <div
          className="rv-host-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Initiative review"
          onClick={() => setReviewOpen(false)}
        >
          <div className="rv-host" onClick={(e) => e.stopPropagation()}>
            <div className="rv-host-head">
              <div>
                <span className="rv-host-kind">Review</span>
                <h2 className="rv-host-title">{reviewingInitiative.title || "Untitled initiative"}</h2>
              </div>
              <button
                type="button"
                className="rv-host-close"
                onClick={() => setReviewOpen(false)}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="rv-host-body">
              <InitiativeReviewActions
                initiative={{
                  id: Number(reviewingInitiative.id),
                  title: reviewingInitiative.title,
                  description: reviewingInitiative.problem || reviewingInitiative.description,
                  submittedBy: reviewingInitiative.submittedBy,
                  businessSponsor: reviewingInitiative.team,
                  created: reviewingInitiative.created,
                  team: reviewingInitiative.team,
                  region: reviewingInitiative.region,
                  problemFrequency: reviewingInitiative.problemFrequency,
                  impactedToday: reviewingInitiative.impactedToday,
                  ifSolved: reviewingInitiative.ifSolved,
                  anticipatedImpact: reviewingInitiative.anticipatedImpact,
                  impactType: reviewingInitiative.impactType,
                  proposedSolution: reviewingInitiative.proposedSolution,
                  existingExamples: reviewingInitiative.existingExamples,
                  dataInputs: reviewingInitiative.dataInputs,
                  constraintsRisks: reviewingInitiative.constraintsRisks,
                  anythingElse: reviewingInitiative.anythingElse,
                }}
                getToken={getToken}
                reviewerUpn={account?.username ?? ""}
                reviewerName={account?.name ?? ""}
                canReview
                onReviewSaved={() => onReviewSaved?.()}
              />
              <p className="rv-host-hint">Save a Business score to unlock the Technical review.</p>
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete item?"
        message={
          <>
            <span>"{item.title}" will be permanently deleted from SharePoint. This cannot be undone.</span>
            {deleteError ? <div className="confirm-error">{deleteError}</div> : null}
          </>
        }
        confirmLabel="Delete"
        tone="danger"
        busy={deleteLoading}
        onConfirm={doDelete}
        onCancel={() => { setDeleteConfirmOpen(false); setDeleteError(null); }}
      />
    </>
  );
}
